import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useProfileQuestions from 'services/general/profileQuestions';
import useSampleInfoQuestions from 'services/surveys/useSampleInfoQuestions';
import useProfileSurveys from 'services/surveys/useProfileSurveys';
import { getShortSurveyTypeformId } from 'views/Information/TypeformSurveysStep';
import { getKitById } from 'store/kits';

export type HealthFormBundleKey = 'profile' | 'sampleInfo' | 'healthHistory';

export type HealthFormBundle = {
  key: HealthFormBundleKey;
  label: string;
  timeEstimate: string;
  description: string;
  done: boolean;
  href: string;
};

export const useHealthFormsSequence = (
  tinyAccountId?: string | null,
  kitId?: string,
) => {
  const kit = useSelector(getKitById(kitId));
  const { config: portalConfig } = usePortalConfigs(kit);
  const surveyType = portalConfig.survey_type ?? 'full';
  const isShortSurvey = surveyType === 'short';
  const isSkipSurvey = surveyType === 'skip';

  const { needsProfile } = useProfileQuestions(tinyAccountId, kitId);
  const { needsSampleInfo } = useSampleInfoQuestions(tinyAccountId, kitId);
  const { data: surveys, needsSurveys } = useProfileSurveys(
    tinyAccountId,
    kitId,
  );

  const shortSurveyTypeformId = useMemo(
    () => (isShortSurvey && kit ? getShortSurveyTypeformId(kit) : undefined),
    [isShortSurvey, kit],
  );

  const firstIncompleteSurveyId = useMemo(
    () =>
      surveys?.find(survey => !survey.is_partner && !survey.is_complete)
        ?.typeform_survey_id,
    [surveys],
  );

  const healthHistoryDone = isShortSurvey
    ? !!kit?.date_survey_completed
    : !needsSurveys;

  const healthHistoryHref = isShortSurvey
    ? shortSurveyTypeformId
      ? `/typeform/${tinyAccountId}/${kitId}/${shortSurveyTypeformId}`
      : `/information/${kitId}`
    : firstIncompleteSurveyId
      ? `/typeform/${tinyAccountId}/${kitId}/${firstIncompleteSurveyId}`
      : `/information/${kitId}`;

  const bundles: HealthFormBundle[] = useMemo(() => {
    const all: HealthFormBundle[] = [
      {
        key: 'profile',
        label: 'Profile',
        timeEstimate: '3 min',
        description: "Tell us a bit about who's taking the test.",
        done: !needsProfile,
        href: `/profile?account=${tinyAccountId}`,
      },
      {
        key: 'sampleInfo',
        label: 'About sample',
        timeEstimate: '3 min',
        description: 'Give us context about your current sample.',
        done: !needsSampleInfo,
        href: `/profile?kit=${kitId}`,
      },
      {
        key: 'healthHistory',
        label: 'Health history',
        timeEstimate: '15 min',
        description: 'Give us context about your health history.',
        done: healthHistoryDone,
        href: healthHistoryHref,
      },
    ];
    return isSkipSurvey ? all.filter(b => b.key !== 'healthHistory') : all;
  }, [
    needsProfile,
    needsSampleInfo,
    healthHistoryDone,
    healthHistoryHref,
    isSkipSurvey,
    tinyAccountId,
    kitId,
  ]);

  const currentIndex = bundles.findIndex(bundle => !bundle.done);

  return { bundles, currentIndex, total: bundles.length };
};

export default useHealthFormsSequence;
